import { Helmet } from 'react-helmet-async';

interface ReviewProps {
  ratingValue: number;
  reviewCount: number;
  bestRating?: number;
  worstRating?: number;
}

interface ProductSchemaProps {
  name: string;
  description: string;
  image: string;
  price: string;
  currency: string;
  availability: 'InStock' | 'OutOfStock' | 'PreOrder';
  sku?: string;
  brand?: string;
  url: string;
  reviews?: ReviewProps;
}

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface FAQItem {
  question: string;
  answer: string;
}

interface SiteNavigationItem {
  name: string;
  url: string;
}

interface HreflangLink {
  lang: string;
  href: string;
}

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  ogType?: 'website' | 'product' | 'article';
  noindex?: boolean;
  // Product structured data
  product?: ProductSchemaProps;
  // Breadcrumbs
  breadcrumbs?: BreadcrumbItem[];
  // FAQ Schema
  faqItems?: FAQItem[];
  // Organization/LocalBusiness
  showOrganization?: boolean;
  // Website schema with Sitelinks
  showWebsiteSchema?: boolean;
  // Site navigation for sitelinks
  siteNavigation?: SiteNavigationItem[];
  // Hreflang for multi-language support
  hreflangLinks?: HreflangLink[];
  // Current page language
  lang?: 'he' | 'en' | 'ar' | 'ru';
}

const SITE_NAME = 'אליס | תכשיטים ושעונים';
const SITE_URL = 'https://alice.jewelry';
const DEFAULT_OG_IMAGE = 'https://storage.googleapis.com/gpt-engineer-file-uploads/ZSufjNmpnwSz0xRwTufg0bdkYfw1/social-images/social-1766485157151-פרופיל גוגל אליס.png';

// Organization structured data (LocalBusiness for jewelry store)
const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'JewelryStore',
  '@id': `${SITE_URL}/#organization`,
  name: 'אליס תכשיטים ושעונים',
  alternateName: ['Alice Jewellery', 'תכשיטי אליס', 'אליס תכשיטים'],
  url: SITE_URL,
  logo: {
    '@type': 'ImageObject',
    url: `${SITE_URL}/images/logo-alice-print.png`,
    width: 512,
    height: 512,
  },
  image: DEFAULT_OG_IMAGE,
  description: 'חברת התכשיטים המובילה בבאר שבע בדרום מאז 1984. באתר התכשיטים אונליין - מבחר ענק של טבעות אירוסין, טבעות נישואין, צמידים, שרשראות, תליונים, עגילים.',
  slogan: 'תכשיטים יוקרתיים מאז 1984',
  telephone: '+972-79-5575576',
  email: 'info@alicejewelry.net',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'רחוב הרצל 58',
    addressLocality: 'באר שבע',
    postalCode: '8423518',
    addressRegion: 'South',
    addressCountry: 'IL',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 31.241175,
    longitude: 34.792080,
  },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Sunday', 'Monday', 'Wednesday', 'Thursday'],
      opens: '10:00',
      closes: '19:00',
    },
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: 'Tuesday',
      opens: '09:45',
      closes: '14:00',
    },
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: 'Friday',
      opens: '09:45',
      closes: '14:00',
    },
  ],
  priceRange: '₪₪-₪₪₪₪',
  currenciesAccepted: 'ILS',
  paymentAccepted: 'Cash, Credit Card, PayPal',
  foundingDate: '1984',
  founder: {
    '@type': 'Person',
    name: 'דוד בורמד',
  },
  sameAs: [
    'https://www.facebook.com/alicejewelry',
    'https://www.instagram.com/alice_jewelry_',
  ],
  hasMap: 'https://www.google.com/maps/place/%D7%90%D7%9C%D7%99%D7%A1+%7C+%D7%AA%D7%9B%D7%A9%D7%99%D7%98%D7%99%D7%9D+%D7%95%D7%A8%D7%A7%D7%95%D7%A8%D7%99%D7%9D/@31.241175,34.792080,17z',
  areaServed: {
    '@type': 'Country',
    name: 'Israel',
  },
  knowsAbout: [
    'טבעות אירוסין',
    'טבעות נישואין',
    'חריטות ועיצוב אישי',
    'תכשיטי ילדים',
    'שרשראות זהב',
    'עגילי זהב',
    'צמידי זהב',
  ],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'קטלוג תכשיטים',
    itemListElement: [
      {
        '@type': 'OfferCatalog',
        name: 'עגילי זהב',
        url: `${SITE_URL}/collections/עגילים`,
      },
      {
        '@type': 'OfferCatalog',
        name: 'שרשראות עם תליון',
        url: `${SITE_URL}/collections/שרשראות`,
      },
      {
        '@type': 'OfferCatalog',
        name: 'טבעות אליס',
        url: `${SITE_URL}/collections/טבעות`,
      },
      {
        '@type': 'OfferCatalog',
        name: 'צמידים',
        url: `${SITE_URL}/collections/צמידים`,
      },
    ],
  },
};

// Default site navigation for sitelinks - matching the screenshot categories
const defaultSiteNavigation: SiteNavigationItem[] = [
  { name: 'עגילי זהב', url: '/collections/עגילים' },
  { name: 'שרשראות עם תליון', url: '/collections/שרשראות' },
  { name: 'טבעות אליס', url: '/collections/טבעות' },
  { name: 'צמידים', url: '/collections/צמידים' },
  { name: 'חנות תכשיטים בבאר שבע, הרצל 58', url: '/contact' },
  { name: 'צמידי אליס', url: '/collections/צמידים' },
  { name: 'שאלות נפוצות', url: '/faq' },
  { name: 'אודות', url: '/about' },
];

// Default hreflang configuration
const getDefaultHreflangLinks = (path: string): HreflangLink[] => [
  { lang: 'he', href: `${SITE_URL}${path}` },
  { lang: 'x-default', href: `${SITE_URL}${path}` },
];

export function SEO({
  title,
  description,
  canonical,
  ogImage,
  ogType = 'website',
  noindex = false,
  product,
  breadcrumbs,
  faqItems,
  showOrganization = false,
  showWebsiteSchema = false,
  siteNavigation,
  hreflangLinks,
  lang = 'he',
}: SEOProps) {
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
  const canonicalUrl = canonical || (typeof window !== 'undefined' ? window.location.href.split('?')[0] : '');
  const ogImageUrl = ogImage || DEFAULT_OG_IMAGE;
  
  // Get hreflang links - use provided or generate defaults
  const finalHreflangLinks = hreflangLinks || getDefaultHreflangLinks(currentPath);

  // Build WebSite schema with SearchAction for Google Sitelinks
  const websiteSchema = showWebsiteSchema
    ? {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        url: SITE_URL,
        name: 'Alice Jewellery',
        alternateName: 'אליס תכשיטים',
        description: 'חנות תכשיטים ושעונים יוקרתיים',
        publisher: {
          '@id': `${SITE_URL}/#organization`,
        },
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${SITE_URL}/shop?search={search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
        inLanguage: 'he-IL',
      }
    : null;

  // Build SiteNavigationElement schema for Sitelinks
  const navItems = siteNavigation || defaultSiteNavigation;
  const siteNavigationSchema = showWebsiteSchema
    ? {
        '@context': 'https://schema.org',
        '@graph': navItems.map((item, index) => ({
          '@type': 'SiteNavigationElement',
          '@id': `${SITE_URL}/#navigation-${index}`,
          name: item.name,
          url: item.url.startsWith('http') ? item.url : `${SITE_URL}${item.url}`,
        })),
      }
    : null;

  // Build product schema with optional reviews
  const productSchema = product
    ? {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: product.name,
        description: product.description,
        image: product.image,
        sku: product.sku,
        brand: product.brand
          ? {
              '@type': 'Brand',
              name: product.brand,
            }
          : undefined,
        offers: {
          '@type': 'Offer',
          url: product.url,
          priceCurrency: product.currency,
          price: product.price,
          availability: `https://schema.org/${product.availability}`,
          seller: {
            '@type': 'Organization',
            name: 'Alice Jewellery',
          },
        },
        ...(product.reviews && {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: product.reviews.ratingValue,
            reviewCount: product.reviews.reviewCount,
            bestRating: product.reviews.bestRating || 5,
            worstRating: product.reviews.worstRating || 1,
          },
        }),
      }
    : null;

  // Build breadcrumb schema
  const breadcrumbSchema =
    breadcrumbs && breadcrumbs.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: breadcrumbs.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            item: item.url.startsWith('http') ? item.url : `${SITE_URL}${item.url}`,
          })),
        }
      : null;

  // Build FAQ schema
  const faqSchema =
    faqItems && faqItems.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqItems.map((item) => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: item.answer,
            },
          })),
        }
      : null;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      
      {/* Canonical */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      
      {/* Language */}
      <html lang={lang} dir={lang === 'he' || lang === 'ar' ? 'rtl' : 'ltr'} />
      
      {/* Hreflang tags for multi-language SEO */}
      {finalHreflangLinks.map((link) => (
        <link key={link.lang} rel="alternate" hrefLang={link.lang} href={link.href} />
      ))}
      
      {/* Robots */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={lang === 'he' ? 'he_IL' : lang === 'ar' ? 'ar_SA' : lang === 'ru' ? 'ru_RU' : 'en_US'} />
      <meta property="og:locale:alternate" content="he_IL" />
      <meta property="og:locale:alternate" content="en_US" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />
      
      {/* Product-specific OG tags */}
      {product && (
        <>
          <meta property="product:price:amount" content={product.price} />
          <meta property="product:price:currency" content={product.currency} />
          <meta property="product:availability" content={product.availability.toLowerCase()} />
        </>
      )}
      
      {/* Structured Data - Organization */}
      {showOrganization && (
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      )}
      
      {/* Structured Data - Product */}
      {productSchema && (
        <script type="application/ld+json">
          {JSON.stringify(productSchema)}
        </script>
      )}
      
      {/* Structured Data - Breadcrumbs */}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
      
      {/* Structured Data - FAQ */}
      {faqSchema && (
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      )}
      
      {/* Structured Data - WebSite with SearchAction */}
      {websiteSchema && (
        <script type="application/ld+json">
          {JSON.stringify(websiteSchema)}
        </script>
      )}
      
      {/* Structured Data - Site Navigation */}
      {siteNavigationSchema && (
        <script type="application/ld+json">
          {JSON.stringify(siteNavigationSchema)}
        </script>
      )}
    </Helmet>
  );
}

export { SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE };
